import { alpha, styled } from '@mui/material/styles';
import { Button } from 'components/ui/Button';
import { getZoomAdjustedThemeSpacingPx } from 'components/ui/utilities/theme';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';

interface DepthStyleProps {
  depth: number;
}

interface SelectedStyleProps {
  isHighlighted?: boolean;
  isSelected?: boolean;
}

interface DragStateStyleProps {
  isDragging?: boolean;
  isDropTarget?: boolean;
  isDraggable?: boolean;
}

interface GroupDropStateStyleProps {
  isDropTarget?: boolean;
}

interface FolderRowStyleProps extends DragStateStyleProps {
  isHighlighted?: boolean;
  isInteractive?: boolean;
}

interface ExpandedStyleProps {
  isExpanded?: boolean;
}

interface ZoomStyleProps {
  zoomLevel: number;
}

interface DragOverlayCardStyleProps extends ZoomStyleProps {
  isDropBlocked?: boolean;
}

const shouldForwardDepthProp = (prop: PropertyKey) => prop !== 'depth';
const shouldForwardExpandedProp = (prop: PropertyKey) => prop !== 'isExpanded';
const shouldForwardSelectedProp = (prop: PropertyKey) => prop !== 'isSelected';
const shouldForwardHighlightedProp = (prop: PropertyKey) => prop !== 'isHighlighted';
const shouldForwardDragStateProp = (prop: PropertyKey) => (
  prop !== 'isDragging'
    && prop !== 'isDropTarget'
    && prop !== 'isDraggable'
);
const shouldForwardGroupDropStateProp = (prop: PropertyKey) => prop !== 'isDropTarget';
const shouldForwardFolderRowProp = (prop: PropertyKey) => (
  shouldForwardDragStateProp(prop) && prop !== 'isInteractive'
);
const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';
const shouldForwardDragOverlayCardProp = (prop: PropertyKey) => (
  shouldForwardZoomProp(prop) && prop !== 'isDropBlocked'
);

export const MeasurementDateDocumentsTreeRoot = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme, zoomLevel }) => ({
  '--checkbox-selection-border-width': getZoomAdjustedPx(1, zoomLevel),
  '--checkbox-selection-check-height': getZoomAdjustedPx(5, zoomLevel),
  '--checkbox-selection-check-width': getZoomAdjustedPx(9, zoomLevel),
  '--checkbox-selection-indeterminate-width': getZoomAdjustedPx(8, zoomLevel),
  '--checkbox-selection-mark-stroke-width': getZoomAdjustedPx(2, zoomLevel),
  '--checkbox-selection-radius': getZoomAdjustedPx(theme.shape.radius.xs, zoomLevel),
  '--checkbox-selection-size': getZoomAdjustedPx(16, zoomLevel),
  '--measurement-date-documents-tree-border-width': getZoomAdjustedPx(1, zoomLevel),
  '--measurement-date-documents-tree-checkbox-size': 'var(--checkbox-selection-size)',
  '--file-inline-rename-action-icon-size': getZoomAdjustedPx(16, zoomLevel),
  '--file-inline-rename-action-size': getZoomAdjustedPx(20, zoomLevel),
  '--file-inline-rename-padding-x': getZoomAdjustedPx(4, zoomLevel),
  '--file-inline-rename-padding-y': getZoomAdjustedPx(2, zoomLevel),
  '--file-inline-rename-radius': getZoomAdjustedPx(theme.shape.radius.xs, zoomLevel),
  '--measurement-date-documents-tree-control-height': getZoomAdjustedPx(16, zoomLevel),
  '--measurement-date-documents-tree-control-width': getZoomAdjustedPx(24, zoomLevel),
  '--measurement-date-documents-tree-file-icon-height': getZoomAdjustedPx(16, zoomLevel),
  '--measurement-date-documents-tree-file-icon-width': getZoomAdjustedPx(24, zoomLevel),
  '--measurement-date-documents-tree-font-size-m': getZoomAdjustedPx(14, zoomLevel),
  '--measurement-date-documents-tree-font-size-s': getZoomAdjustedPx(12, zoomLevel),
  '--measurement-date-documents-tree-gap-xs': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--measurement-date-documents-tree-line-height-m': getZoomAdjustedPx(20, zoomLevel),
  '--measurement-date-documents-tree-line-height-s': getZoomAdjustedPx(16, zoomLevel),
  '--measurement-date-documents-tree-metadata-max-width': getZoomAdjustedPx(520, zoomLevel),
  '--measurement-date-documents-tree-radius-xs': getZoomAdjustedPx(theme.shape.radius.xs, zoomLevel),
  '--measurement-date-documents-tree-row-height': getZoomAdjustedPx(28, zoomLevel),
  '--measurement-date-documents-tree-row-padding-end': getZoomAdjustedPx(12, zoomLevel),
  '--measurement-date-documents-tree-row-padding-start': getZoomAdjustedPx(16, zoomLevel),
  '--measurement-date-documents-tree-spacing-xs': getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  '--measurement-date-documents-tree-spacing-s': getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel),
  '--measurement-date-documents-tree-spacing-m': getZoomAdjustedThemeSpacingPx(theme, 'm', zoomLevel),
  color: theme.palette.neutral[500],
  containerType: 'inline-size',
  display: 'grid',
  gap: 'var(--measurement-date-documents-tree-gap-xs)',
  minWidth: 0,
  position: 'relative',
}));

export const MeasurementDateDocumentsTreeGroup = styled('div')(({ theme }) => ({
  backgroundColor: 'transparent',
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  boxShadow: 'none',
  display: 'grid',
  gap: 'var(--measurement-date-documents-tree-gap-xs)',
  minWidth: 0,
  position: 'relative',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeInOut,
  }),
  '@media (prefers-reduced-motion: reduce)': {
    transitionDuration: '0ms',
  },
}));

export const MeasurementDateDocumentsTreeDropTargetOverlay = styled('div', {
  shouldForwardProp: shouldForwardGroupDropStateProp,
})<GroupDropStateStyleProps>(({ isDropTarget = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.brand[100], 0.46),
  border: `var(--measurement-date-documents-tree-border-width) dashed ${theme.palette.brand[400]}`,
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  boxShadow: `inset 0 0 0 var(--measurement-date-documents-tree-border-width) ${alpha(
    theme.palette.brand[400],
    0.36
  )}, 0 0 0 calc(var(--measurement-date-documents-tree-border-width) * 2) ${alpha(
    theme.palette.brand[400],
    0.1
  )}`,
  color: theme.palette.brand[800],
  display: 'flex',
  gap: 'var(--measurement-date-documents-tree-spacing-s)',
  inset: 0,
  justifyContent: 'center',
  opacity: isDropTarget ? 1 : 0,
  padding: 'var(--measurement-date-documents-tree-spacing-s)',
  pointerEvents: 'none',
  position: 'absolute',
  transform: isDropTarget ? 'scale(1)' : 'scale(0.99)',
  transition: theme.transitions.create(['opacity', 'transform'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeInOut,
  }),
  zIndex: 5,
  '@media (prefers-reduced-motion: reduce)': {
    transitionDuration: '0ms',
  },
}));

export const MeasurementDateDocumentsTreeDropTargetOverlayIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.brand[100],
  borderRadius: '50%',
  color: theme.palette.brand[500],
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--measurement-date-documents-tree-line-height-m)',
  height: 'calc(var(--measurement-date-documents-tree-row-height) + var(--measurement-date-documents-tree-spacing-xs))',
  justifyContent: 'center',
  width: 'calc(var(--measurement-date-documents-tree-row-height) + var(--measurement-date-documents-tree-spacing-xs))',
}));

export const MeasurementDateDocumentsTreeDropTargetOverlayText = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.common.white, 0.9),
  border: `var(--measurement-date-documents-tree-border-width) solid ${alpha(theme.palette.brand[300], 0.56)}`,
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  boxShadow: `0 calc(var(--measurement-date-documents-tree-border-width) * 8) calc(var(--measurement-date-documents-tree-border-width) * 20) ${alpha(
    theme.palette.brand[900],
    0.12
  )}`,
  display: 'flex',
  gap: 'var(--measurement-date-documents-tree-spacing-xs)',
  maxWidth: 'min(360px, calc(100% - var(--measurement-date-documents-tree-spacing-m)))',
  minWidth: 0,
  padding: 'var(--measurement-date-documents-tree-spacing-xs) var(--measurement-date-documents-tree-spacing-s)',
}));

export const MeasurementDateDocumentsTreeDropTargetOverlayTitle = styled('span')(({ theme }) => ({
  color: theme.palette.brand[800],
  display: '-webkit-box',
  fontSize: 'var(--measurement-date-documents-tree-font-size-m)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--measurement-date-documents-tree-line-height-m)',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  '& strong': {
    color: theme.palette.brand[800],
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

export const MeasurementDateDocumentsTreeRootDropTarget = styled('div', {
  shouldForwardProp: shouldForwardDragStateProp,
})<DragStateStyleProps>(({ isDropTarget = false, theme }) => ({
  alignItems: 'center',
  backgroundColor: isDropTarget ? alpha(theme.palette.brand[100], 0.42) : 'transparent',
  border: `var(--measurement-date-documents-tree-border-width) dashed ${
    isDropTarget ? theme.palette.brand[400] : 'transparent'
  }`,
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  boxShadow: isDropTarget
    ? `0 0 0 calc(var(--measurement-date-documents-tree-border-width) * 2) ${
      alpha(theme.palette.brand[400], 0.16)
    }`
    : '0 0 0 0 transparent',
  color: isDropTarget ? theme.palette.brand[800] : theme.palette.text.secondary,
  display: 'grid',
  fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  inset: 0,
  opacity: isDropTarget ? 1 : 0,
  placeItems: 'center',
  lineHeight: 'var(--measurement-date-documents-tree-line-height-s)',
  pointerEvents: 'auto',
  position: 'absolute',
  transform: isDropTarget ? 'translateY(0) scale(1.01)' : 'translateY(0) scale(1)',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color', 'opacity', 'transform'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeInOut,
  }),
  zIndex: isDropTarget ? 6 : 0,
  animation: 'measurementDateDocumentsTreeRootDropTargetIn 160ms ease-out',
  '@keyframes measurementDateDocumentsTreeRootDropTargetIn': {
    from: {
      opacity: 0,
      transform: 'translateY(calc(var(--measurement-date-documents-tree-spacing-xs) * -1)) scale(0.98)',
    },
    to: {
      opacity: 1,
      transform: isDropTarget ? 'translateY(0) scale(1.01)' : 'translateY(0) scale(1)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transitionDuration: '0ms',
  },
}));

export const MeasurementDateDocumentsTreeRow = styled('div', {
  shouldForwardProp: prop => (
    shouldForwardDepthProp(prop)
      && shouldForwardHighlightedProp(prop)
      && shouldForwardSelectedProp(prop)
      && shouldForwardDragStateProp(prop)
  ),
})<DepthStyleProps & DragStateStyleProps & SelectedStyleProps>(({
  depth,
  isDragging = false,
  isHighlighted = false,
  isSelected = false,
  theme,
}) => {
  const isEmphasized = isSelected || isHighlighted;

  return {
    alignItems: 'center',
    backgroundColor: isEmphasized ? theme.palette.brand[100] : theme.palette.background.paper,
    border: `var(--measurement-date-documents-tree-border-width) solid ${
      isEmphasized ? theme.palette.brand[500] : 'transparent'
    }`,
    borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
    boxSizing: 'border-box',
    color: isEmphasized ? theme.palette.brand[800] : theme.palette.neutral[500],
    display: 'grid',
    gap: 'var(--measurement-date-documents-tree-gap-xs)',
    gridTemplateColumns: 'auto auto auto minmax(0, 1fr) minmax(0, min(38cqi, var(--measurement-date-documents-tree-metadata-max-width))) auto',
    minHeight: 'var(--measurement-date-documents-tree-row-height)',
    minWidth: 0,
    opacity: isDragging ? 0.55 : 1,
    outline: 0,
    padding: 'var(--measurement-date-documents-tree-spacing-xs) var(--measurement-date-documents-tree-row-padding-end)',
    paddingLeft: `calc(var(--measurement-date-documents-tree-row-padding-start) + ${
      depth
    } * var(--measurement-date-documents-tree-spacing-m))`,
    position: 'relative',
    transition: theme.transitions.create(['background-color', 'border-color', 'color', 'opacity'], {
      duration: theme.transitions.duration.shortest,
    }),
    width: '100%',
    zIndex: 1,
    '&:hover, &:focus-within': {
      backgroundColor: isEmphasized ? theme.palette.brand[100] : theme.palette.neutral[100],
      borderColor: isEmphasized ? theme.palette.brand[500] : 'transparent',
      color: isEmphasized ? theme.palette.brand[800] : theme.palette.neutral[700],
    },
    '[data-firm-documents-dragging="true"] &:hover': {
      backgroundColor: isEmphasized ? theme.palette.brand[100] : theme.palette.background.paper,
      borderColor: isEmphasized ? theme.palette.brand[500] : 'transparent',
      color: isEmphasized ? theme.palette.brand[800] : theme.palette.neutral[500],
    },
    '[data-firm-documents-dragging="true"] &': {
      pointerEvents: 'none',
    },
    '&:focus-within': {
      backgroundColor: theme.palette.brand[100],
      borderColor: isEmphasized ? theme.palette.brand[600] : theme.palette.brand[400],
      color: theme.palette.brand[800],
    },
    '&:hover [data-row-action], &:focus-within [data-row-action]': {
      opacity: 1,
    },
    '&:hover [data-file-drag-handle], &:focus-within [data-file-drag-handle]': {
      opacity: 1,
    },
    '[data-firm-documents-dragging="true"] &:hover [data-row-action]': {
      opacity: 0,
    },
    '[data-firm-documents-dragging="true"] &:hover [data-file-drag-handle]': {
      opacity: 0,
    },
    '@container (max-width: 720px)': {
      gridTemplateColumns: 'auto auto auto minmax(0, 1fr) auto',
      '& [data-firm-document-file-metadata="true"]': {
        display: 'none',
      },
    },
  };
});

export const MeasurementDateDocumentsTreeFolderRow = styled(MeasurementDateDocumentsTreeRow, {
  shouldForwardProp: shouldForwardFolderRowProp,
})<FolderRowStyleProps>(({
  isHighlighted = false,
  isInteractive = true,
  theme,
}) => ({
  backgroundColor: isHighlighted ? theme.palette.brand[100] : theme.palette.background.paper,
  borderColor: isHighlighted ? theme.palette.brand[500] : 'transparent',
  boxShadow: 'none',
  cursor: isInteractive ? 'pointer' : 'default',
  color: isHighlighted ? theme.palette.brand[800] : theme.palette.neutral[500],
  fontWeight: theme.typography.fontWeightRegular,
  gridTemplateColumns: 'auto auto minmax(0, 1fr) auto auto',
  outline: 0,
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&:focus-within': {
    backgroundColor: isHighlighted ? theme.palette.brand[100] : theme.palette.background.paper,
    borderColor: isHighlighted ? theme.palette.brand[600] : 'transparent',
    color: isHighlighted ? theme.palette.brand[800] : theme.palette.neutral[500],
  },
  '&:hover': {
    backgroundColor: isHighlighted ? theme.palette.brand[100] : theme.palette.neutral[100],
    borderColor: isHighlighted ? theme.palette.brand[500] : 'transparent',
    color: isHighlighted ? theme.palette.brand[800] : theme.palette.neutral[700],
  },
  '[data-firm-documents-dragging="true"] &:hover': {
    backgroundColor: isHighlighted ? theme.palette.brand[100] : theme.palette.background.paper,
    borderColor: isHighlighted ? theme.palette.brand[500] : 'transparent',
    color: isHighlighted ? theme.palette.brand[800] : theme.palette.neutral[500],
  },
  '&:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    borderColor: theme.palette.brand[400],
    color: theme.palette.neutral[700],
  },
  '@media (prefers-reduced-motion: reduce)': {
    transitionDuration: '0ms',
  },
  '@container (max-width: 720px)': {
    gridTemplateColumns: 'auto auto minmax(0, 1fr) auto auto',
  },
}));

export const MeasurementDateDocumentsTreeDragOverlayCard = styled('div', {
  shouldForwardProp: shouldForwardDragOverlayCardProp,
})<DragOverlayCardStyleProps>(({ isDropBlocked = false, theme, zoomLevel }) => ({
  '--measurement-date-documents-tree-file-icon-height': getZoomAdjustedPx(16, zoomLevel),
  '--measurement-date-documents-tree-file-icon-width': getZoomAdjustedPx(24, zoomLevel),
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  border: `${getZoomAdjustedPx(1, zoomLevel)} solid ${
    isDropBlocked ? theme.palette.error.main : theme.palette.brand[400]
  }`,
  borderRadius: getZoomAdjustedPx(theme.shape.radius.s, zoomLevel),
  boxShadow: `0 ${getZoomAdjustedPx(14, zoomLevel)} ${getZoomAdjustedPx(34, zoomLevel)} ${
    alpha(theme.palette.common.black, 0.15)
  }`,
  boxSizing: 'border-box',
  color: isDropBlocked ? theme.palette.error.main : theme.palette.brand[800],
  cursor: isDropBlocked ? 'not-allowed' : 'grabbing',
  display: 'grid',
  gap: getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel),
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  lineHeight: getZoomAdjustedPx(18, zoomLevel),
  maxWidth: getZoomAdjustedPx(360, zoomLevel),
  minHeight: getZoomAdjustedPx(36, zoomLevel),
  minWidth: getZoomAdjustedPx(220, zoomLevel),
  padding: `${getZoomAdjustedThemeSpacingPx(theme, 'xs', zoomLevel)} ${
    getZoomAdjustedThemeSpacingPx(theme, 's', zoomLevel)
  }`,
  pointerEvents: 'none',
  transform: `translateY(${getZoomAdjustedPx(4, zoomLevel)})`,
}));

export const MeasurementDateDocumentsTreeDragOverlayText = styled('span')({
  display: 'grid',
  minWidth: 0,
});

export const MeasurementDateDocumentsTreeDragOverlayTitle = styled('span')(({ theme }) => ({
  color: theme.palette.neutral[700],
  fontSize: 'inherit',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'inherit',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const MeasurementDateDocumentsTreeDragOverlaySubtitle = styled('span')(({ theme }) => ({
  color: theme.palette.brand[600],
  fontSize: '0.86em',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'inherit',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '& strong': {
    color: theme.palette.brand[700],
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const MeasurementDateDocumentsTreeFolderControlIcon = styled('span')({
  alignItems: 'center',
  color: 'inherit',
  display: 'inline-flex',
  height: 'var(--measurement-date-documents-tree-control-height)',
  justifyContent: 'center',
  width: 'var(--measurement-date-documents-tree-control-width)',
  '& svg': {
    display: 'block',
    fontSize: 'var(--measurement-date-documents-tree-control-height)',
  },
});

export const MeasurementDateDocumentsTreeFolderToggleIcon = styled(MeasurementDateDocumentsTreeFolderControlIcon, {
  shouldForwardProp: shouldForwardExpandedProp,
})<ExpandedStyleProps>(({ isExpanded = false, theme }) => ({
  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export const MeasurementDateDocumentsTreeChildren = styled('div', {
  shouldForwardProp: shouldForwardDepthProp,
})<DepthStyleProps>(({ depth }) => ({
  display: 'grid',
  gap: 'var(--measurement-date-documents-tree-gap-xs)',
  minWidth: 0,
  paddingLeft: depth === 0 ? 0 : 'var(--measurement-date-documents-tree-spacing-xs)',
  position: 'relative',
}));

export const MeasurementDateDocumentsTreeIcon = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  height: 'var(--measurement-date-documents-tree-control-height)',
  justifyContent: 'center',
  width: 'var(--measurement-date-documents-tree-control-width)',
  '& svg': {
    display: 'block',
    fontSize: 'var(--measurement-date-documents-tree-control-height)',
  },
});

export const MeasurementDateDocumentsTreeFileIcon = styled(MeasurementDateDocumentsTreeIcon)({
  flex: '0 0 var(--measurement-date-documents-tree-file-icon-width)',
  height: 'var(--measurement-date-documents-tree-file-icon-height)',
  width: 'var(--measurement-date-documents-tree-file-icon-width)',
  '& svg': {
    fontSize: 'var(--measurement-date-documents-tree-file-icon-height)',
    maxHeight: 'var(--measurement-date-documents-tree-file-icon-height)',
    maxWidth: 'var(--measurement-date-documents-tree-file-icon-height)',
  },
});

export const MeasurementDateDocumentsTreeDragHandle = styled('button')(({ theme }) => ({
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  color: theme.palette.neutral[400],
  cursor: 'grab',
  display: 'inline-flex',
  height: 'var(--measurement-date-documents-tree-control-height)',
  justifyContent: 'center',
  opacity: 0.46,
  padding: 0,
  transition: theme.transitions.create(['background-color', 'color', 'opacity'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'var(--measurement-date-documents-tree-control-height)',
  '& svg': {
    display: 'block',
    fontSize: 'var(--measurement-date-documents-tree-control-height)',
  },
  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.neutral[100],
    color: theme.palette.neutral[700],
    opacity: 1,
    outline: 0,
  },
  '&:active': {
    cursor: 'grabbing',
  },
  '&:disabled': {
    color: theme.palette.neutral[300],
    cursor: 'default',
    opacity: 0,
  },
}));

export const MeasurementDateDocumentsTreeAiFileIcon = styled('img')({
  borderRadius: '50%',
  display: 'block',
  height: 'var(--measurement-date-documents-tree-file-icon-height)',
  objectFit: 'contain',
  width: 'var(--measurement-date-documents-tree-file-icon-height)',
});

export const MeasurementDateDocumentsTreeText = styled('span')(({ theme }) => ({
  color: 'inherit',
  fontSize: 'var(--measurement-date-documents-tree-font-size-m)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--measurement-date-documents-tree-line-height-m)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const MeasurementDateDocumentsTreeFileMetadata = styled('span')({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'var(--measurement-date-documents-tree-gap-xs)',
  justifySelf: 'end',
  maxWidth: 'min(38cqi, var(--measurement-date-documents-tree-metadata-max-width))',
  minWidth: 0,
  overflow: 'hidden',
});

export const MeasurementDateDocumentsTreeMetadataChip = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[100],
  border: `var(--measurement-date-documents-tree-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  color: theme.palette.neutral[600],
  display: 'inline-flex',
  flex: '0 1 auto',
  fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  gap: 'calc(var(--measurement-date-documents-tree-spacing-xs) / 2)',
  lineHeight: 'var(--measurement-date-documents-tree-line-height-s)',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  padding: '0 var(--measurement-date-documents-tree-spacing-xs)',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  whiteSpace: 'nowrap',
  '& [data-firm-document-metadata-chip-value="true"]': {
    color: theme.palette.neutral[700],
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& [data-firm-document-metadata-chip-icon="true"]': {
    alignItems: 'center',
    display: 'inline-flex',
    flex: '0 0 auto',
    fontSize: 'var(--measurement-date-documents-tree-font-size-m)',
  },
}));

export const MeasurementDateDocumentsTreeSearchHighlight = styled('mark')(({ theme }) => ({
  backgroundColor: alpha(theme.palette.warning.main, 0.18),
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  color: 'inherit',
  fontWeight: 'inherit',
  padding: '0 calc(var(--measurement-date-documents-tree-spacing-xs) / 2)',
}));

export const MeasurementDateDocumentsTreeFolderName = styled(MeasurementDateDocumentsTreeText)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightRegular,
}));

export const MeasurementDateDocumentsTreeFolderLabel = styled('span')({
  alignItems: 'center',
  display: 'flex',
  gap: 'var(--measurement-date-documents-tree-gap-xs)',
  minWidth: 0,
});

export const MeasurementDateDocumentsTreeFolderEmptyBadge = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[50],
  border: `var(--measurement-date-documents-tree-border-width) solid ${theme.palette.neutral[200]}`,
  borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
  color: theme.palette.neutral[500],
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--measurement-date-documents-tree-line-height-s)',
  padding: '0 var(--measurement-date-documents-tree-spacing-xs)',
}));

export const MeasurementDateDocumentsTreeSkeletonRow = styled(MeasurementDateDocumentsTreeRow)(({ theme }) => ({
  backgroundColor: theme.palette.neutral[100],
  '&:hover, &:focus-within': {
    backgroundColor: theme.palette.neutral[100],
    borderColor: 'transparent',
    color: theme.palette.neutral[500],
  },
}));

export const MeasurementDateDocumentsTreeState = styled('div')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.secondary,
  display: 'grid',
  gap: 'var(--measurement-date-documents-tree-gap-xs)',
  justifyItems: 'center',
  padding: 'var(--measurement-date-documents-tree-spacing-m)',
  textAlign: 'center',
}));

export const MeasurementDateDocumentsTreeStateTitle = styled('strong')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 'var(--measurement-date-documents-tree-line-height-s)',
}));

export const MeasurementDateDocumentsTreeStateDescription = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 'var(--measurement-date-documents-tree-line-height-s)',
}));

export const MeasurementDateDocumentsTreeStateActions = styled('div')({
  alignItems: 'center',
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: 'var(--measurement-date-documents-tree-gap-xs)',
  justifyContent: 'center',
});

export const MeasurementDateDocumentsTreeStateActionButton = styled(Button)(({ theme }) => ({
  '&, &.MuiButton-root': {
    borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
    fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 'var(--measurement-date-documents-tree-line-height-s)',
    minHeight: 'var(--measurement-date-documents-tree-row-height)',
    padding: 'var(--measurement-date-documents-tree-spacing-xs) var(--measurement-date-documents-tree-spacing-s)',
    textTransform: 'none',
  },
}));

export const MeasurementDateDocumentsTreeShowMoreButton = styled(Button)(({ theme }) => ({
  alignSelf: 'center',
  marginTop: 'var(--measurement-date-documents-tree-spacing-xs)',
  minHeight: 'var(--measurement-date-documents-tree-row-height)',
  padding: 'var(--measurement-date-documents-tree-spacing-xs) var(--measurement-date-documents-tree-spacing-s)',
  textTransform: 'none',
  '&, &.MuiButton-text': {
    borderRadius: 'var(--measurement-date-documents-tree-radius-xs)',
    color: theme.palette.brand[500],
    fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 'var(--measurement-date-documents-tree-line-height-s)',
    minHeight: 'var(--measurement-date-documents-tree-row-height)',
    padding: 'var(--measurement-date-documents-tree-spacing-xs) var(--measurement-date-documents-tree-spacing-s)',
    textTransform: 'none',
  },
  '&.Mui-disabled, &.MuiButton-text.Mui-disabled': {
    backgroundColor: 'transparent',
    color: theme.palette.action.disabled,
    cursor: 'progress',
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    marginLeft: 0,
    marginRight: 0,
  },
  '& .MuiButton-startIcon > *:first-of-type, & .MuiButton-endIcon > *:first-of-type, & svg': {
    fontSize: 'var(--measurement-date-documents-tree-font-size-s)',
  },
}));
